import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  Avatar,
  Grid
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import BuildIcon from '@mui/icons-material/Build';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLanguage } from '../utils/LanguageContext';

// App version
const APP_VERSION = '1.0.0';

// FAQ Data
const FAQ_DATA = [
  {
    question: "How do I add a new construction phase?",
    answer: "Navigate to 'Phase Settings' tab and click on 'Add New Phase' button. Enter the name of your new phase and save it. You can now track expenses for this phase."
  },
  {
    question: "Can I customize the default phases?",
    answer: "Yes, you can edit, delete, or reorder the default phases from the 'Phase Settings' tab. You can also reset to the default phases if needed."
  },
  {
    question: "How is the budget calculated?",
    answer: "You can set a budget for each phase in the 'Budget Management' tab. The app tracks expenses against these budgets and shows utilization percentages."
  },
  {
    question: "How do I add transportation costs?",
    answer: "When adding an expense, toggle the 'Include Transportation Fee' switch and enter the transportation cost. The app will include this in the total cost calculation."
  },
  {
    question: "Can I export my data?",
    answer: "Yes, you can export expense data to Excel format from the 'Reports & Export' tab or directly from the expense list. The exported file can be edited later in any spreadsheet software."
  },
  {
    question: "Is my data secure?",
    answer: "Your data is stored locally on your device using browser's localStorage. It's not transmitted to any server, ensuring your financial information remains private."
  }
];

const Settings = ({ toggleDarkMode, darkMode }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, language } = useLanguage();

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleShowAbout = () => {
    setShowAbout(true);
  };

  const handleBackToSettings = () => {
    setShowAbout(false);
  };

  if (showAbout) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBackToSettings}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ mr: 2 }}
          >
            {t('settings.backToSettings')}
          </Button>
          <Typography variant={isMobile ? "h6" : "h5"}>
            {t('settings.aboutThisApp')}
          </Typography>
        </Box>
        
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0 }}>
                <HomeWorkIcon />
              </Avatar>
              <Typography variant="h6">
                RumahKira
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
              by hnlykci &copy; 2025 Project
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body1" paragraph>
              {t('settings.appDescription.part1')}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {t('settings.appDescription.part2')}
            </Typography>
            
            <Typography variant="body1" paragraph>
              {t('settings.appDescription.part3')}
            </Typography>
          </CardContent>
        </Card>
        
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('settings.keyFeatures')}
        </Typography>
        
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant={isMobile ? "subtitle1" : "h6"}>{t('settings.phaseTracking')}</Typography>
              </Box>
              <Typography variant="body2">
                {t('settings.phaseTrackingDesc')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant={isMobile ? "subtitle1" : "h6"}>{t('settings.budgetManagement')}</Typography>
              </Box>
              <Typography variant="body2">
                {t('settings.budgetManagementDesc')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant={isMobile ? "subtitle1" : "h6"}>{t('settings.expenseTracking')}</Typography>
              </Box>
              <Typography variant="body2">
                {t('settings.expenseTrackingDesc')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          Thank you for using RumahKira. I hope it helps you as much as it helped me in managing construction expenses effectively.
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
          &copy; 2025 hnlykci. All rights reserved.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
      <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
        {t('nav.settings')}
      </Typography>
      
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <DarkModeIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.darkMode')}
              secondary={t('settings.switchTheme')}
            />
            <Switch 
              checked={darkMode}
              onChange={toggleDarkMode}
              color="primary"
            />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemIcon>
              <InfoIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.appVersion')}
              secondary={`${t('settings.currentVersion')}: ${APP_VERSION}`}
            />
            <Chip label={APP_VERSION} color="primary" variant="outlined" size="small" />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemIcon>
              <InfoIcon />
            </ListItemIcon>
            <ListItemText 
              primary={t('settings.about')}
              secondary={t('settings.viewInfoAboutApp')}
            />
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleShowAbout}
              size={isMobile ? "small" : "medium"}
            >
              {t('settings.viewAbout')}
            </Button>
          </ListItem>
        </List>
      </Paper>
      
      <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
        {t('settings.supportFaq')}
      </Typography>
      
      <Paper elevation={2} sx={{ mb: 4 }}>
        {FAQ_DATA.map((faq, index) => (
          <Accordion 
            key={index} 
            expanded={expanded === `panel${index}`} 
            onChange={handleAccordionChange(`panel${index}`)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}bh-content`}
              id={`panel${index}bh-header`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant={isMobile ? "body1" : "subtitle1"}>{faq.question}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Box>
  );
};

export default Settings; 